import { takeLatest, put, call, all, fork } from 'redux-saga/effects';
import TransferEmployeeTypes from './TransferEmployee.types';
import {
	fetchTransferEmployeeData,
	fetchAllTransfersData,
	deleteTransferData,
} from './TransferEmployee.services';
import {
	transferEmployeeSuccess,
	transferEmployeeFailure,
	getAllTransfersSuccess,
	getAllTransfersFailure,
	deleteTransferSuccess,
	deleteTransferFailure,
} from './TransferEmployee.action';
import AlertManager from '../../utils/alertManager';
import { encryptData } from '../_common/enode-decode';

export function* fetchTransferEmployeeDataSaga(payload: any) {
	try {
		const key = 'fetchTransferEmployeeKey';
		const encryptedData = encryptData(payload.payload, key);
		// @ts-ignore
		const response = yield call(fetchTransferEmployeeData, encryptedData);
		if (process.env.NODE_ENV === 'development') console.log('Transfer response:', response);
		if (process.env.NODE_ENV === 'development')
			console.log('Transfer response.data:', response.data);
		if (process.env.NODE_ENV === 'development')
			console.log('Full backend response (response.data):', response.data);
		if (process.env.NODE_ENV === 'development')
			console.log('Transfer response.data.status:', response.data?.status);
		if (process.env.NODE_ENV === 'development')
			console.log('Transfer response.status:', response.status);
		if (response.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (response?.data?.status === 'error') {
				if (process.env.NODE_ENV === 'development')
					console.log('Received failure response');
				yield put(transferEmployeeFailure(response.data));
			} else if (response?.data?.status === 'success') {
				if (process.env.NODE_ENV === 'development')
					console.log('Received success response');
				yield put(transferEmployeeSuccess(response.data));
			} else {
				if (process.env.NODE_ENV === 'development')
					console.log('Received unknown response, treating as success');
				yield put(transferEmployeeSuccess(response.data));
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development')
			console.error('Error in fetchTransferEmployeeData:', error);
		yield put(
			transferEmployeeFailure(
				error?.response?.data || { status: 'error', message: 'An error occurred' },
			),
		);
	}
}

export function* fetchAllTransfersDataSaga(payload: any) {
	try {
		const key = 'fetchAllTransfersKey';
		const encryptedData = encryptData(payload.payload, key);
		if (process.env.NODE_ENV === 'development')
			console.log('fetchAllTransfersDataSaga: Starting with payload:', payload);
		// @ts-ignore
		const response = yield call(fetchAllTransfersData, encryptedData);
		if (process.env.NODE_ENV === 'development')
			console.log('Get All Transfers response:', response);
		if (process.env.NODE_ENV === 'development')
			console.log('Get All Transfers response.data:', response.data);
		if (response.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			// Check if response has encrypted data structure (success) or error structure
			if (response?.data?.status === 'error') {
				if (process.env.NODE_ENV === 'development')
					console.log('Received failure response');
				// Error responses are not encrypted, pass as is
				yield put(getAllTransfersFailure(response.data));
			} else if (response?.data?.status === 'success' && response?.data?.response) {
				if (process.env.NODE_ENV === 'development')
					console.log('Received success response with encrypted data');
				// Success responses are encrypted, pass entire response.data to reducer for decryption
				yield put(getAllTransfersSuccess(response.data));
			} else {
				if (process.env.NODE_ENV === 'development')
					console.log(
						'Received unknown response structure, checking for encrypted response',
					);
				// Try to handle as encrypted response if it has response field
				if (response?.data?.response) {
					yield put(getAllTransfersSuccess(response.data));
				} else {
					if (process.env.NODE_ENV === 'development')
						console.log('Unknown response structure, treating as error');
					yield put(
						getAllTransfersFailure(
							response.data || {
								status: 'error',
								message: 'Unknown response structure',
							},
						),
					);
				}
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development')
			console.error('Error in fetchAllTransfersData:', error);
		yield put(
			getAllTransfersFailure(
				error?.response?.data || { status: 'error', message: 'An error occurred' },
			),
		);
	}
}

export function* deleteTransferDataSaga(payload: any) {
	try {
		const key = 'deleteTransferKey';
		const encryptedData = encryptData(payload.payload, key);
		// @ts-ignore
		const response = yield call(deleteTransferData, encryptedData);
		if (process.env.NODE_ENV === 'development')
			console.log('Delete Transfer response:', response);
		if (response.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (response?.data?.status === 'error') {
				if (process.env.NODE_ENV === 'development')
					console.log('Received failure response');
				yield put(deleteTransferFailure(response.data));
			} else if (response?.data?.status === 'success') {
				if (process.env.NODE_ENV === 'development')
					console.log('Received success response');
				yield put(deleteTransferSuccess(response.data));
			} else {
				if (process.env.NODE_ENV === 'development')
					console.log('Received unknown response, treating as success');
				yield put(deleteTransferSuccess(response.data));
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development')
			console.error('Error in deleteTransferData:', error);
		yield put(
			deleteTransferFailure(
				error?.response?.data || { status: 'error', message: 'An error occurred' },
			),
		);
	}
}

export function* onTransferEmployeeStart(): Generator<any, void, any> {
	yield takeLatest(
		TransferEmployeeTypes.TRANSFER_EMPLOYEE_START as any,
		fetchTransferEmployeeDataSaga,
	);
}

export function* onGetAllTransfersStart(): Generator<any, void, any> {
	if (process.env.NODE_ENV === 'development')
		console.log('onGetAllTransfersStart: Setting up listener for GET_ALL_TRANSFERS_START');
	yield takeLatest(
		TransferEmployeeTypes.GET_ALL_TRANSFERS_START as any,
		fetchAllTransfersDataSaga,
	);
}

export function* onDeleteTransferStart(): Generator<any, void, any> {
	yield takeLatest(TransferEmployeeTypes.DELETE_TRANSFER_START as any, deleteTransferDataSaga);
}

export function* transferEmployeeDetailsSaga(): Generator<any, void, any> {
	if (process.env.NODE_ENV === 'development')
		console.log('transferEmployeeDetailsSaga: Starting saga');
	yield all([
		fork(onTransferEmployeeStart),
		fork(onGetAllTransfersStart),
		fork(onDeleteTransferStart),
	]);
	if (process.env.NODE_ENV === 'development')
		console.log('transferEmployeeDetailsSaga: All sagas forked');
}
